package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ThemeArgument
import top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeValue
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.compiler.theme.ColorMath

/**
 * 主题值解析 Pass：把 theme token 中的「token 引用别名」[ThemeValue.TokenRef] 与
 * 「颜色函数调用」[ThemeValue.ColorCall] 解析为字面量（String/Double/Boolean），
 * 保证后续派生与 IR 发射只见到原始字面量。
 *
 * - 引用别名：`primary = secondary` → primary 取 secondary 的字面量；支持链式引用与前向引用。
 *   引用成环报 E799、目标未定义报 E800（均不中断编译）。
 * - 颜色函数：`lighten(c,amt)` / `darken(c,amt)` / `alpha(c,a)` / `mix(c1,c2,t)`，
 *   编译期用 [ColorMath] 求值为 #AARRGGBB。参数类型错误报 E797、函数名/参数个数错误报 E798。
 *
 * 运行时机：注册表侧在 [ThemeInheritancePass] 之后（解析引用到继承父 token / 颜色函数作种子）
 * 与 [DerivativeThemePass] 之后（解析引用到派生 token）各跑一次，幂等；screen 侧在 [ThemeMergePass]
 * 合并后、派生前解析。无 [ThemeValue] 的主题零开销跳过。
 */
class ThemeValueResolverPass(private val diag: DiagnosticCollector) {

    /** 解析注册表中所有 scheme 的 token 值（幂等：已是字面量者跳过）。 */
    fun resolveRegistry(registry: ThemeRegistry, positionOf: Map<String, SourcePosition>, fallback: SourcePosition) {
        for (scheme in registry.schemes().toList()) {
            val tokens = registry.lookup(scheme) ?: continue
            if (tokens.values.none { it is ThemeValue }) continue
            registry.registerWithNodes(
                scheme,
                resolve(LinkedHashMap(tokens), registry.tokenNodes(scheme), positionOf[scheme] ?: fallback),
                registry.tokenNodes(scheme),
            )
        }
    }

    /**
     * 解析单个 token map：把所有 [ThemeValue] 折叠为字面量。无法解析的条目将被移除（已报错）。
     */
    fun resolve(tokens: LinkedHashMap<String, Any>, pos: SourcePosition): LinkedHashMap<String, Any> =
        resolve(tokens, emptyMap(), pos)

    internal fun resolve(
        tokens: LinkedHashMap<String, Any>,
        tokenNodes: Map<String, ThemeTokenAst>,
        fallback: SourcePosition,
    ): LinkedHashMap<String, Any> {
        if (tokens.values.none { it is ThemeValue }) return tokens
        val out = LinkedHashMap<String, Any>(tokens)
        val resolved = HashMap<String, Any?>()
        val visiting = LinkedHashSet<String>()

        fun resolveKey(key: String): Any? {
            if (resolved.containsKey(key)) return resolved[key]
            val raw = out[key] ?: return null
            if (raw !is ThemeValue) {
                resolved[key] = raw
                return raw
            }
            val keyPosition = tokenNodes[key]?.valuePosition ?: raw.position.takeIf { it.sourceId.isNotEmpty() } ?: fallback
            if (key in visiting) {
                diag.error(DslError.E799_THEME_TOKEN_ALIAS_CYCLE, keyPosition, key)
                resolved[key] = null
                return null
            }
            visiting.add(key)
            val value = resolveValue(raw, key, keyPosition, ::resolveKey)
            visiting.remove(key)
            resolved[key] = value
            return value
        }

        for (key in out.keys.toList()) {
            val value = resolveKey(key)
            if (value != null) out[key] = value else out.remove(key)
        }
        return out
    }

    private fun resolveValue(v: ThemeValue, key: String, pos: SourcePosition, resolveKey: (String) -> Any?): Any? =
        when (v) {
            is ThemeValue.TokenRef -> {
                val target = resolveKey(v.name)
                if (target == null) {
                    diag.error(DslError.E800_THEME_TOKEN_ALIAS_UNRESOLVED, actualPosition(v.position, pos), key, v.name)
                    null
                } else {
                    target
                }
            }

            is ThemeValue.ColorCall -> evalColor(v, key, actualPosition(v.position, pos), resolveKey)
        }

    private fun resolveArg(arg: Any, key: String, pos: SourcePosition, resolveKey: (String) -> Any?): Any? =
        when (arg) {
            is ThemeArgument -> resolveArgumentValue(arg, key, resolveKey)
            is ThemeValue.TokenRef -> {
                val target = resolveKey(arg.name)
                if (target == null) {
                    diag.error(DslError.E800_THEME_TOKEN_ALIAS_UNRESOLVED, actualPosition(arg.position, pos), key, arg.name)
                    null
                } else {
                    target
                }
            }

            is ThemeValue.ColorCall -> evalColor(arg, key, actualPosition(arg.position, pos), resolveKey)
            else -> arg
        }

    private fun resolveArgumentValue(
        argument: ThemeArgument,
        key: String,
        resolveKey: (String) -> Any?,
    ): Any? = when (val value = argument.value) {
        is ThemeValue.TokenRef -> {
            val target = resolveKey(value.name)
            if (target == null) {
                diag.error(DslError.E800_THEME_TOKEN_ALIAS_UNRESOLVED, argument.position, key, value.name)
                null
            } else {
                target
            }
        }
        is ThemeValue.ColorCall -> evalColor(value, key, argument.position, resolveKey)
        else -> value
    }

    private fun evalColor(
        call: ThemeValue.ColorCall,
        key: String,
        pos: SourcePosition,
        resolveKey: (String) -> Any?,
    ): String? {
        val argv = call.args.map { resolveArg(it, key, pos, resolveKey) }
        if (argv.any { it == null }) return null // 参数解析已报错

        fun argumentPosition(index: Int): SourcePosition =
            (call.args.getOrNull(index) as? ThemeArgument)?.position ?: pos

        fun color(i: Int): String? {
            val a = argv[i]
            if (a is String) return a
            diag.error(DslError.E797_COLOR_FN_ARG_TYPE, argumentPosition(i), call.name, "第 ${i + 1} 个参数应为颜色")
            return null
        }

        fun number(i: Int): Double? {
            val a = argv[i]
            if (a is Number) return a.toDouble()
            diag.error(DslError.E797_COLOR_FN_ARG_TYPE, argumentPosition(i), call.name, "第 ${i + 1} 个参数应为数字")
            return null
        }

        fun arity(n: Int): Boolean {
            if (argv.size != n) {
                diag.error(DslError.E798_COLOR_FN_UNKNOWN_OR_ARITY, pos, call.name, "需要 $n 个参数，实际 ${argv.size}")
                return false
            }
            return true
        }

        return try {
            when (call.name) {
                "lighten" -> if (arity(2)) color(0)?.let { c -> number(1)?.let { a -> ColorMath.lighten(c, a) } } else null
                "darken" -> if (arity(2)) color(0)?.let { c -> number(1)?.let { a -> ColorMath.darken(c, a) } } else null
                "alpha" -> if (arity(2)) color(0)?.let { c -> number(1)?.let { a -> ColorMath.withAlpha(c, a) } } else null
                "mix" -> if (arity(3)) color(0)?.let { c1 -> color(1)?.let { c2 -> number(2)?.let { t -> ColorMath.alphaBlend(c1, c2, t) } } } else null
                else -> {
                    diag.error(DslError.E798_COLOR_FN_UNKNOWN_OR_ARITY, pos, call.name, "未知颜色函数")
                    null
                }
            }
        } catch (e: IllegalArgumentException) {
            // ColorMath.parseArgb 对非法颜色字面量抛出
            diag.error(DslError.E797_COLOR_FN_ARG_TYPE, pos, call.name, e.message ?: "颜色解析失败")
            null
        }
    }

    private fun actualPosition(position: SourcePosition, fallback: SourcePosition): SourcePosition =
        if (position.sourceId.isNotEmpty()) position else fallback
}
